import {Button, Card, Checkbox, Dropdown, Flex, Input, Modal, Popover, Space} from "antd";
import Title from "antd/es/typography/Title";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAngleDown,
    faAngleUp, faArrowUpWideShort,
    faPencil,
    faPlus,
    faRotate,
    faTrash
} from "@fortawesome/free-solid-svg-icons";
import {categories, combineSettingDescription} from "./list/SettingList";
import {Setting} from "../../setting/Setting";
import {useParams} from "react-router-dom";
import {
    generateSettingComponentPrompt,
    generateSettingComponentsPrompt,
    improveSettingComponentPrompt
} from "./prompts/ComponentsPrompts";
import TextArea from "antd/es/input/TextArea";
import {
    DraftComponent,
    DraftComponentCategory,
    embeddedComponents,
    embeddedElements
} from "../../../../../../feature/Draft";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {aiChainPrompt, similaritySearch, unembed} from "../../../../../../ai/Prompt";
import {
    addComponent, deleteComponent,
    deleteSettingCategory,
    updateComponent, updateElement, updateElements,
    updateSettingCategory
} from "../../../../../../feature/project/draftSlice";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {EmbedButton} from "../../../../../../ai/components/EmbedButton";
import {EmbeddingErrorMessage} from "../../../../../../ai/components/EmbeddingErrorMessage";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import React from "react";

export const SettingCategoryDetails = (
    props: { data: DraftComponentCategory, onDeleted: () => void }
) => {
    const draft = useAppSelector(state => state.draftSlice.draft)
    const model = useAppSelector(state => state.draftSlice.model)
    const {projectId} = useParams()
    const [showEmbeddingWarning, setShowEmbeddingWarning] = React.useState<'list' | 'single' | 'extend' | undefined>(undefined)
    const [showDeleteEmbeddingWarning, setShowDeleteEmbeddingWarning] = React.useState<DraftComponent | undefined>(undefined)
    const [showElementsWarning, setShowElementsWarning] = React.useState(false)
    const [showDeleteEmbeddingCategoryWarning, setShowDeleteEmbeddingCategoryWarning] = React.useState(false)
    const [unembedding, setUnembedding] = React.useState(false)
    const [generating, setGenerating] = React.useState(false)
    const [generatingSingle, setGeneratingSingle] = React.useState(false)
    const [expanded, setExpanded] = React.useState(false)
    const [loading, setLoading] = React.useState<string | undefined>(undefined)
    const [open, setOpen] = React.useState<string | undefined>(undefined)
    const [prompt, setPrompt] = React.useState('')
    const [useContext, setUseContext] = React.useState(true)

    const dispatch = useAppDispatch()

    const generateComponents = async (category: Setting, force?: boolean) => {
        if (embeddedComponents(props.data.components) == false) {
            if (props.data.components.length == 0 || force == true) {
                let context = await similaritySearch({
                    project: projectId,
                    query: props.data.description ?? '',
                    where: {'\'type\'': 'setting'}
                })
                let data = await aiChainPrompt({
                    projectId: projectId ?? "",
                    prompt: generateSettingComponentsPrompt({
                        category: category,
                        genres: draft.properties.genre,
                        synopsis: draft.properties.synopsis,
                        setting: draft.setting.description,
                        context: context?.documents
                    }),
                    model: model,
                    type: 'json'
                })

                dispatch(
                    updateSettingCategory({
                            category: {
                                ...props.data,
                                components: data.data.map((c: any) => ({...c, embedded: false, elements: []})),
                            }
                        }
                    )
                )
            } else {
                setShowElementsWarning(true)
            }
        } else {
            setShowEmbeddingWarning('list')
        }
    }

    const generateComponent = async (component?: DraftComponent) => {
        const category = categories.find(c => c.key == props.data.category)!
        const query = component ? component.description : props.data.description
        let context = query ? await similaritySearch({
            project: projectId,
            query: query,
            where: {'type': 'setting'}
        }) : undefined
        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: generateSettingComponentPrompt({
                category: category,
                genres: draft.properties.genre,
                customPrompt: category.prompt.component.single + '\n\nКатегория: ' + props.data.title,
                synopsis: draft.properties.synopsis,
                setting: draft.setting.description,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })


        if (component) {
            dispatch(updateComponent({
                // @ts-ignore
                category: props.data.category,
                component: {
                    ...component,
                    description: data.data.description,
                    elements: []
                },
            }))
        } else {
            dispatch(addComponent({
                // @ts-ignore
                category: props.data.category,
                component: {
                    ...data.data,
                    id: props.data.components.flat().sort((a, b) => b.id - a.id)[0]?.id + 1 || 1,
                    elements: []
                }
            }))
        }
    }

    const improveComponent = async (component: DraftComponent) => {
        const categoryData = categories.find(c => c.key == props.data.category)!
        let context = useContext ? await similaritySearch({
            project: projectId,
            query: (props.data.description ?? '') + '\n' + prompt,
            where: {'type': 'setting'}
        }) : undefined

        let data = await aiChainPrompt({
            projectId: projectId ?? "",
            prompt: improveSettingComponentPrompt({
                customPrompt: prompt ?? (categoryData.prompt.component.single + '\n\nКомпонент: ' + component.title),
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                setting: draft.setting.description,
                category: props.data,
                component: component,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateComponent({
            // @ts-ignore
            category: props.data.category,
            component: {
                ...component,
                description: data.data.description,
            }
        }))
    }

    const addEmptyComponent = async () => {
        dispatch(addComponent({
            // @ts-ignore
            category: props.data.category,
            component: {
                title: '',
                description: '',
                id: props.data.components.flat().sort((a, b) => b.id - a.id)[0]?.id + 1 || 1,
                elements: []
            }
        }))
    }

    return (
        <>
            <Flex gap={10} vertical={true} style={{overflowY: 'auto', scrollbarWidth: "none"}}>
                <Card title={<Flex justify={'space-between'}>{props.data?.title}
                    <Flex gap={10} align={'center'}><Button
                        icon={<FontAwesomeIcon icon={faTrash}/>}
                        style={{background: '#fff'}}
                        onClick={() => {
                            if (embeddedComponents(props.data.components) == false) {
                                dispatch(deleteSettingCategory({
                                    categoryKey: props.data.category
                                }))
                                props.onDeleted()
                            } else {
                                setShowDeleteEmbeddingCategoryWarning(true)
                            }
                        }}/>
                        <FontAwesomeIcon
                            style={{
                                background: "#ffffff",
                                border: "1px solid #eee",
                                borderRadius: 20,
                                padding: 5,
                                aspectRatio: 1,
                                cursor: 'pointer'
                            }}
                            color={'#000'}
                            icon={!expanded ? faAngleDown : faAngleUp}
                            onClick={(e) => {
                                setExpanded(!expanded)
                            }}/></Flex></ Flex>}
                      styles={{body: {whiteSpace: 'break-spaces'}}}>
                    {expanded ? combineSettingDescription(categories.find(c => c.key == props.data?.category)) : categories.find(c => c.key == props.data?.category)?.description}
                </Card>
                <Card>
                    <Flex justify={'space-between'} align={'center'} wrap={'wrap'}>
                        <Title level={5} style={{margin: 0}}>Components</Title>
                        <Space>
                            <GenButton
                                style={{width: 130}}
                                type={'primary'}
                                loading={generating}
                                actions={[{
                                    key: "1", title: "Generate", action: async (model: string) => {
                                        const category = categories.find(c => c.key == props.data.category)
                                        if (category) {
                                            await generateComponents(category!)
                                                .catch((e) => {
                                                    console.log(e)
                                                })
                                        }

                                    }
                                }]}
                            />

                            <Dropdown
                                trigger={['click']}
                                menu={{
                                    items: [
                                        {
                                            key: "empty",
                                            label: <Space><FontAwesomeIcon icon={faPlus} style={{width: 16}}/>New empty
                                                component</Space>
                                        },
                                        {
                                            key: "gen",
                                            label: <Space><BoltIcon secondary={faPlus} size={16}/>Generate new
                                                component</Space>,
                                        }
                                    ],
                                    onClick: async (e) => {
                                        if (e.key == 'empty') {
                                            await addEmptyComponent()
                                        } else if (e.key == 'gen') {
                                            setGeneratingSingle(true)
                                            await generateComponent().finally(() => {
                                                setGeneratingSingle(false)
                                            })

                                        }
                                    }
                                }}
                            >
                                <Button
                                    loading={generatingSingle}
                                    icon={<FontAwesomeIcon icon={faPlus}/>}
                                />
                            </Dropdown>
                        </Space>
                    </Flex>
                </Card>
                <Space direction={'vertical'}>
                    {props.data?.components?.map((item, index) =>
                        <Card style={{flexGrow: 1}} key={index}
                              title={<Flex justify={'space-between'}>
                                  <Flex gap={10} style={{flexGrow: 1}}>
                                      <div style={{
                                          height: 30,
                                          minWidth: 30,
                                          width: 30,
                                          borderRadius: 30,
                                          background: "#ffffff",
                                          color: "#000",
                                          textAlign: "center",
                                          padding: 3
                                      }}>{index + 1}</div>
                                      <DebounceInput
                                          style={{fontWeight: 'bold'}}
                                          placeholder={"Component title"}
                                          value={item.title}
                                          onChange={(text) => {
                                              dispatch(updateComponent({
                                                  category: props.data.category ?? '',
                                                  component: {
                                                      ...item,
                                                      title: text
                                                  }
                                              }))
                                          }}/>

                                      <Space>
                                          <Popover
                                              placement={'bottomRight'}
                                              content={
                                                  <Space direction={'vertical'}>
                                                      <TextArea onChange={(e) => setPrompt(e.target.value)}
                                                                value={prompt}
                                                                autoSize={true}
                                                                style={{width: 300}}
                                                      />
                                                      <Checkbox checked={useContext} onChange={(e) => {
                                                          setUseContext(e.target.checked)
                                                      }}>Include context</Checkbox>
                                                      <Button
                                                          type={'primary'}
                                                          onClick={async () => {
                                                              if (embeddedElements(item.elements) != false) {
                                                                  setShowEmbeddingWarning('extend')
                                                              } else {
                                                                  setLoading("element" + item.id)
                                                                  setOpen(undefined)
                                                                  await improveComponent(item)
                                                                  setLoading(undefined)
                                                              }
                                                          }}>Go</Button>
                                                  </Space>
                                              }
                                              title="Prompt"
                                              trigger="click"
                                              open={open == "element-" + item.id}
                                              onOpenChange={(e) => {
                                                  setOpen(e ? "element-" + item.id : undefined)
                                              }}
                                          >
                                              <GenButton
                                                  mode={'icon'}
                                                  hideMainAction={true}
                                                  loading={loading == "element" + item.id}
                                                  open={open == undefined}
                                                  icon={<IconBoltGrad/>}
                                                  actions={[{
                                                      key: "1",
                                                      title: "Re-generate",
                                                      action: async (model: string) => {
                                                          if (embeddedElements(item.elements) != false) {
                                                              setShowEmbeddingWarning('single')
                                                          } else {
                                                              await generateComponent(item)
                                                                  .catch((e) => {
                                                                      console.log(e)
                                                                  })
                                                          }
                                                      },
                                                      render: (key, title, action) => {
                                                          return <Space><BoltIcon secondary={faRotate}
                                                                                  size={16}/>{title}</Space>
                                                      }
                                                  },
                                                      {
                                                          key: "2", title: "Improve", action: async (model: string) => {
                                                              if (embeddedElements(item.elements) != false) {
                                                                  setShowEmbeddingWarning('extend')
                                                              } else {
                                                                  await improveComponent(item)
                                                              }
                                                          }, render: (key, title, action) => {
                                                              return <Flex gap={10} justify={'space-between'}>
                                                                  <Space><BoltIcon secondary={faArrowUpWideShort}
                                                                                   size={16}/>{title}</Space><Button
                                                                  type={'text'} size={'small'}
                                                                  icon={<FontAwesomeIcon icon={faPencil}/>}
                                                                  onClick={(e) => {
                                                                      setPrompt(categories.find(c => c.key == props.data.category)?.prompt.element.extend + '\n\nЭлемент: ' + item.title)
                                                                      e.stopPropagation()
                                                                      setOpen("element-" + item.id)
                                                                  }}/>
                                                              </Flex>
                                                          }
                                                      }]
                                                  }/>

                                          </Popover>
                                          {item.elements?.filter(e => e.description && e.description.trim().length > 0).length > 0 ?
                                              <EmbedButton
                                                  data={
                                                      item.elements.map(e => ({
                                                          id: props.data.category + '-component' + item.id + '-element' + e.id,
                                                          text: e.description ?? '',
                                                          metadata:
                                                              {
                                                                  title: e.title ?? '',
                                                                  category: props.data.category ?? '',
                                                                  type: 'setting'
                                                              },

                                                      }))}
                                                  chunkSize={800}
                                                  chunkOverlap={100}
                                                  embedded={embeddedElements(item.elements)}
                                                  edited={false}
                                                  onEmbedded={(data) => {
                                                      const id = parseInt(data.id.split('-')[2].replace('element', ''))
                                                      dispatch(updateElement({
                                                          category: props.data.category,
                                                          componentId: item.id,
                                                          element: {
                                                              ...item.elements.find(e => e.id == id)!,
                                                              embedded: true
                                                          }
                                                      }))
                                                  }}
                                                  onUnembedded={(data) => {
                                                      const d = Array.isArray(data) ? data : [data]
                                                      d.forEach(data => {
                                                          const id = parseInt(data.id.split('-')[2].replace('element', ''))
                                                          dispatch(updateElement({
                                                              category: props.data.category,
                                                              componentId: item.id,
                                                              element: {
                                                                  ...item.elements.find(e => e.id == id)!,
                                                                  embedded: false
                                                              }
                                                          }))
                                                      })
                                                  }}
                                              />
                                              : ''}
                                          <Button
                                              icon={<FontAwesomeIcon icon={faTrash}/>}
                                              style={{background: '#fff'}}
                                              onClick={() => {
                                                  if (embeddedElements(item.elements) == false) {
                                                      dispatch(deleteComponent({
                                                          // @ts-ignore
                                                          category: props.data.category ?? '',
                                                          componentId: item.id
                                                      }))
                                                  } else {
                                                      setShowDeleteEmbeddingWarning(item)
                                                  }
                                              }}/>
                                      </Space>
                                  </Flex>
                              </Flex>}>

                            <TextAreaActions
                                style={{border: "none"}}
                                autoSize={true}
                                value={item.description}
                                validate={text => true}
                                onChange={(text: string) => {
                                    dispatch(updateComponent({
                                        category: props.data.category ?? '',
                                        component: {
                                            ...item,
                                            description: text
                                        }
                                    }))
                                }}
                            />
                        </Card>
                    )}
                </Space>
            </Flex>
            {showEmbeddingWarning ? <EmbeddingErrorMessage
                onClose={() => setShowEmbeddingWarning(undefined)}
                content={<>
                    <div>You are trying to regenerate {showEmbeddingWarning == 'list' ? 'components' : 'component'} with
                        embedded elements:
                    </div>
                    <ul>
                        {props.data.components.filter(e => embeddedElements(e.elements) != false).map(e => <li
                            style={{fontWeight: '700'}}>{e.title}</li>)}
                    </ul>
                    <div>You have to un-embed elements before editing</div>
                </>}
                loading={unembedding}
                onUnmbedClick={() => {
                    setUnembedding(true)
                    unembed({
                        project: projectId,
                        ids: props.data.components.map(c => ({
                            id: c.id,
                            elements: c.elements.filter(e => e.embedded)
                        })).map(e => e.elements.map(el => props.data.category + '-component' + e.id + '-element' + el.id)).flat()
                    })
                        .then(() => {
                            props.data.components.forEach(component => {
                                dispatch(updateElements({
                                    category: props.data.category,
                                    componentId: component.id,
                                    elements: component.elements.filter(e => e.embedded).map(e => ({
                                        ...e,
                                        embedded: false
                                    }))
                                }))
                            })

                        }).finally(() => {
                        setUnembedding(false)
                        setShowEmbeddingWarning(undefined)
                    })
                }}
            /> : ''}
            {showDeleteEmbeddingWarning ? <EmbeddingErrorMessage
                onClose={() => setShowDeleteEmbeddingWarning(undefined)}
                content={<>
                    <div>You are trying to delete components with embedded elements:</div>
                    <ul>
                        {showDeleteEmbeddingWarning.elements.filter(e => e.embedded).map(e => <li
                            style={{fontWeight: '700'}}>{e.title}</li>)}
                    </ul>
                    <div>You have to un-embed them before deleting</div>
                </>}
                loading={unembedding}
                onUnmbedClick={() => {
                    setUnembedding(true)
                    unembed({
                        project: projectId,
                        ids: showDeleteEmbeddingWarning.elements.filter(e => e.embedded).map(el => props.data.category + '-component' + showDeleteEmbeddingWarning.id + '-element' + el.id)
                    })
                        .then(() => {
                            dispatch(updateElements({
                                category: props.data.category,
                                componentId: showDeleteEmbeddingWarning.id,
                                elements: showDeleteEmbeddingWarning.elements.filter(e => e.embedded).map(e => ({
                                    ...e,
                                    embedded: false
                                }))
                            }))

                        }).finally(() => {
                        setUnembedding(false)
                        setShowDeleteEmbeddingWarning(undefined)
                    })
                }}
            /> : ''}
            {showDeleteEmbeddingCategoryWarning ? <EmbeddingErrorMessage
                onClose={() => setShowDeleteEmbeddingCategoryWarning(false)}
                content={<>
                    <div>You are trying to delete category with embedded elements</div>
                    <div>You have to un-embed them before deleting</div>
                </>}
                loading={unembedding}
                onUnmbedClick={() => {
                    setUnembedding(true)
                    unembed({
                        project: projectId,
                        ids: props.data.components.map(c => ({
                            ...c,
                            elements: c.elements.filter(e => e.embedded)
                        })).map(c => c.elements.map(el => props.data.category + '-component' + c.id + '-element' + el.id)).flat()
                    })
                        .then(() => {
                            props.data.components.forEach(component => {
                                dispatch(updateElements({
                                    category: props.data.category,
                                    componentId: component.id,
                                    elements: component.elements.filter(e => e.embedded).map(e => ({
                                        ...e,
                                        embedded: false
                                    }))
                                }))
                            })
                        }).finally(() => {
                        setUnembedding(false)
                        setShowDeleteEmbeddingCategoryWarning(false)
                    })
                }}
            /> : ''}
            {showElementsWarning ? <Modal
                open={true}
                title={'Generation Warning'}
                cancelText={'Cancel'}
                okText={'Generate'}
                onCancel={() => setShowElementsWarning(false)}
                onOk={async () => {
                    const category = categories.find(c => c.key == props.data.category)
                    setShowElementsWarning(false)
                    setGenerating(true)
                    await generateComponents(category!, true)
                        .finally(() => {
                            setGenerating(false)
                        })

                }}
            >
                <Space direction={'vertical'}>
                    <div style={{whiteSpace: 'pre-wrap'}}>You already have generated components for this category.
                        <ul>
                            {props.data.components.map(e => <li style={{fontWeight: 700}}>{e.title}</li>)}
                        </ul>
                        If you choose <strong>Generate</strong> option then existing <strong>components</strong> with
                        their <strong>elements</strong> will be replaced with newly generated data. You will not be able
                        to undone this action.
                    </div>
                </Space>
            </Modal> : ''}
        </>
    )
}